// pills.tsx — Small colored badges. One export per shape:
// StatusPill (active / suspended), RolePill (owner / admin /
// member), ActionPill (audit action), OutcomePill (success /
// rejected). Pure presentational; no Firestore types leak in
// here.

interface StatusProps {
  status: string;
}

export function StatusPill({ status }: StatusProps) {
  const label = status === 'suspended' ? 'Suspended' : 'Active';
  const cls = status === 'suspended' ? 'is-suspended' : 'is-active';
  return <span className={`console-status ${cls}`}>{label}</span>;
}

interface RoleProps {
  role: string;
}

export function RolePill({ role }: RoleProps) {
  const label = (role || 'member').toUpperCase();
  const color =
    role === 'owner'
      ? 'var(--gold-metallic, #D4AF37)'
      : role === 'admin'
      ? 'var(--accent-magenta)'
      : 'var(--text-secondary)';
  return (
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
        color,
      }}
    >
      {label}
    </span>
  );
}

interface ActionProps {
  action: string;
}

export function ActionPill({ action }: ActionProps) {
  let group: keyof typeof PALETTE = 'other';
  if (action.startsWith('seat_code_')) group = 'code';
  else if (action.startsWith('seat_')) group = 'seat';
  else if (action.startsWith('member_')) group = 'invite';
  else if (action.startsWith('org_')) group = 'org';
  const { fg, bg } = PALETTE[group];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 999,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        fontWeight: 600,
        color: fg,
        background: bg,
      }}
    >
      {action}
    </span>
  );
}

const PALETTE = {
  code: { fg: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)' },
  seat: { fg: '#f472b6', bg: 'rgba(244, 114, 182, 0.12)' },
  invite: { fg: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)' },
  org: { fg: '#facc15', bg: 'rgba(250, 204, 21, 0.14)' },
  other: { fg: 'var(--text-secondary)', bg: 'rgba(255, 255, 255, 0.04)' },
} as const;

interface OutcomeProps {
  outcome: string;
}

export function OutcomePill({ outcome }: OutcomeProps) {
  const cls = outcome === 'rejected' ? 'is-suspended' : 'is-active';
  return <span className={`console-status ${cls}`}>{outcome || '?'}</span>;
}
